// Register.jsx
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { Zap } from 'lucide-react';

export function Register() {
  const [form, setForm]       = useState({ name: '', email: '', password: '', company: '' });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate     = useNavigate();

  const handle = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return toast.error('Password must be at least 8 characters');
    setLoading(true);
    try {
      await register(form.name, form.email, form.password, form.company);
      toast.success('Account created! Please verify your email.');
      navigate('/verify-email');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo"><Zap size={22} fill="currentColor" style={{ display: 'inline', marginRight: 6 }} />Wispy</div>
        <h1 className="auth-title">Create your account</h1>
        <p className="auth-sub">Start your 14-day free trial — no credit card needed</p>

        <form onSubmit={handle} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {[
            { key: 'name',     label: 'Full name',    type: 'text',     placeholder: 'John Smith',         required: true  },
            { key: 'email',    label: 'Email',         type: 'email',    placeholder: 'you@company.com',    required: true  },
            { key: 'company',  label: 'Company',       type: 'text',     placeholder: 'Acme Inc. (optional)',required: false },
            { key: 'password', label: 'Password',      type: 'password', placeholder: 'Min 8 characters',   required: true  },
          ].map(f => (
            <div className="form-group" key={f.key}>
              <label className="form-label">{f.label}</label>
              <input className="input" type={f.type} placeholder={f.placeholder} required={f.required}
                value={form[f.key]} onChange={e => setForm(v => ({ ...v, [f.key]: e.target.value }))} />
            </div>
          ))}

          <button className="btn btn-primary btn-lg" type="submit" disabled={loading} style={{ width: '100%', marginTop: 6 }}>
            {loading ? <><span className="spinner" style={{ width: 16, height: 16 }}/> Creating account…</> : 'Create free account'}
          </button>
        </form>

        <div className="divider"/>
        <p style={{ textAlign: 'center', fontSize: 14, color: 'var(--text2)' }}>
          Already have an account? <Link to="/login" style={{ color: 'var(--brand)', textDecoration: 'none', fontWeight: 500 }}>Sign in</Link>
        </p>
      </div>
    </div>
  );
}

// ForgotPassword.jsx
export function ForgotPassword() {
  const [email, setEmail]   = useState('');
  const [sent, setSent]     = useState(false);
  const [loading, setLoading] = useState(false);
  const { authAPI: _a } = {}; // import at top in real split
  const { default: api } = require('../services/api'); // eslint-disable-line

  const handle = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setSent(true);
    } catch {
      setSent(true); // always show success to prevent enumeration
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo"><Zap size={22} fill="currentColor" style={{ display: 'inline', marginRight: 6 }} />Wispy</div>
        <h1 className="auth-title">Reset password</h1>
        {sent ? (
          <div style={{ color: 'var(--text2)', fontSize: 14, lineHeight: 1.7 }}>
            ✅ If that email exists, a reset link has been sent. Check your inbox.
            <div style={{ marginTop: 20 }}><Link to="/login" style={{ color: 'var(--brand)', textDecoration: 'none', fontWeight: 500 }}>← Back to login</Link></div>
          </div>
        ) : (
          <form onSubmit={handle} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ color: 'var(--text2)', fontSize: 14, marginBottom: 4 }}>Enter your email and we'll send you a reset link.</p>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="input" type="email" placeholder="you@company.com" value={email}
                onChange={e => setEmail(e.target.value)} required />
            </div>
            <button className="btn btn-primary btn-lg" type="submit" disabled={loading} style={{ width: '100%' }}>
              {loading ? 'Sending…' : 'Send reset link'}
            </button>
            <Link to="/login" style={{ textAlign: 'center', fontSize: 13, color: 'var(--text3)', textDecoration: 'none' }}>← Back to login</Link>
          </form>
        )}
      </div>
    </div>
  );
}

// ResetPassword.jsx
export function ResetPassword() {
  const [form, setForm]       = useState({ password: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone]       = useState(false);
  const { useParams, useNavigate: _n } = require('react-router-dom'); // eslint-disable-line
  const { token } = useParams();
  const navigate  = useNavigate();
  const { default: api } = require('../services/api'); // eslint-disable-line

  function useNavigate() { return _n ? _n() : () => {}; }

  const handle = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) return toast.error('Passwords do not match');
    setLoading(true);
    try {
      await api.put(`/auth/reset-password/${token}`, { password: form.password });
      setDone(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };
  const { default: toast } = require('react-hot-toast'); // eslint-disable-line

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo"><Zap size={22} fill="currentColor" style={{ display: 'inline', marginRight: 6 }} />Wispy</div>
        <h1 className="auth-title">Set new password</h1>
        {done ? <p style={{ color: '#4ecf88' }}>✅ Password reset! Redirecting to login…</p> : (
          <form onSubmit={handle} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[['password','New password'],['confirm','Confirm password']].map(([k,l]) => (
              <div className="form-group" key={k}>
                <label className="form-label">{l}</label>
                <input className="input" type="password" placeholder="••••••••" value={form[k]}
                  onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} required minLength={8} />
              </div>
            ))}
            <button className="btn btn-primary btn-lg" type="submit" disabled={loading} style={{ width: '100%' }}>
              {loading ? 'Resetting…' : 'Reset password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// VerifyEmail.jsx
export function VerifyEmail() {
  const [otp, setOtp]         = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const { useAuth: _a }       = require('../context/AuthContext'); // eslint-disable-line
  const { authAPI }           = require('../services/api'); // eslint-disable-line
  const { default: toast }    = require('react-hot-toast'); // eslint-disable-line
  const { useNavigate: _n }   = require('react-router-dom'); // eslint-disable-line
  const navigate = _n();
  const auth     = _a();

  const handle = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authAPI.verifyEmail({ otp });
      await auth.loadUser();
      toast.success('Email verified!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await authAPI.resendOtp();
      toast.success('OTP resent to your email');
    } catch {
      toast.error('Failed to resend');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo"><Zap size={22} fill="currentColor" style={{ display: 'inline', marginRight: 6 }} />Wispy</div>
        <h1 className="auth-title">Verify your email</h1>
        <p style={{ color: 'var(--text2)', fontSize: 14, marginBottom: 24 }}>
          We sent a 6-digit code to your email. Enter it below.
        </p>
        <form onSubmit={handle} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="form-group">
            <label className="form-label">Verification code</label>
            <input className="input" type="text" placeholder="123456" value={otp} onChange={e => setOtp(e.target.value)}
              maxLength={6} style={{ fontSize: 22, letterSpacing: 8, textAlign: 'center' }} required />
          </div>
          <button className="btn btn-primary btn-lg" type="submit" disabled={loading} style={{ width: '100%' }}>
            {loading ? 'Verifying…' : 'Verify email'}
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={resend} disabled={resending}
            style={{ alignSelf: 'center', color: 'var(--text3)' }}>
            {resending ? 'Sending…' : "Didn't get it? Resend code"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default Register;
